<template>
  <b-modal
    id="marketing-prefs-modal"
    title="Edit Marketing Preferences"
    centered
    size="md"
    body-class="p-0 mp-modal-body-scroll"
    footer-class="border-0"
    cancel-variant="outline-secondary"
    ok-title="Save"
    :ok-disabled="saving || loading"
    :cancel-disabled="saving"
    @shown="fetchSubscription"
    @hidden="resetState"
    @ok.prevent="save"
  >
    <div v-if="loading" class="mp-loading">
      <b-spinner variant="primary" />
    </div>
    <div v-else class="mp-body">
      <!-- GLOBAL UNSUBSCRIBE -->
      <div
        class="mp-card mp-card-unsubscribe"
        :class="{ 'mp-card-danger': unsubscribed }"
      >
        <div>
          <div class="mp-title mb-0">Global Unsubscribe</div>
          <p class="mp-derived-desc mt-1 mb-0">
            When enabled, user receives NO messages regardless of topic
            preferences
          </p>
        </div>
        <b-form-checkbox v-model="unsubscribed" switch class="mb-0" />
      </div>

      <!-- SUBSCRIPTION TOPICS -->
      <p class="mp-section-label mt-3">SUBSCRIPTION TOPICS</p>

      <div v-for="topic in subscriptionTopics" :key="topic.key" class="mp-card">
        <div>
          <div class="mp-title">{{ topic.title }}</div>
          <div class="mp-meta">
            <span class="mp-chip">{{ topic.key }}</span>
            <span class="mp-arrow">→</span>
            <span class="mp-chip">{{ topic.listName }}</span>
            <span class="mp-synced">✓ Synced</span>
          </div>
        </div>
        <b-form-checkbox v-model="topic.active" switch class="mb-0" />
      </div>

      <!-- MARKETING OPT-IN -->
      <p class="mp-section-label mt-4">MARKETING OPT-IN</p>

      <div class="mp-card">
        <div>
          <div class="mp-title">Email Marketing</div>
          <span class="mp-chip">email_marketing_opt_in</span>
        </div>
        <b-form-checkbox v-model="emailMarketing" switch class="mb-0" />
      </div>

      <div class="mp-card">
        <div>
          <div class="mp-title">SMS Marketing</div>
          <span class="mp-chip">sms_marketing_opt_in</span>
        </div>
        <b-form-checkbox v-model="smsMarketing" switch class="mb-0" />
      </div>

      <div class="mp-card mp-card-derived">
        <div>
          <div
            class="d-flex align-items-center"
            style="gap: 8px; margin-bottom: 4px"
          >
            <span class="mp-title mb-0">Marketing Opt-in</span>
            <span class="mp-derived-badge">DERIVED</span>
          </div>
          <p class="mp-derived-desc">
            Auto-computed: false only when both email &amp; SMS are false
          </p>
          <span class="mp-chip">marketing_opt_in</span>
        </div>
        <span class="mp-derived-value">{{
          marketingOptIn ? "true" : "false"
        }}</span>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BFormCheckbox, BSpinner } from "bootstrap-vue";
import { mapState, mapActions } from "vuex";

export default {
  components: { BModal, BFormCheckbox, BSpinner },
  data() {
    return {
      unsubscribed: false,
      subscriptionTopics: [
        {
          key: "topic_3",
          listName: "newsletters",
          title: "Newsletters",
          apiKey: "newsletters",
          active: false,
        },
        {
          key: "topic_5",
          listName: "racing_promos",
          title: "Exclusive Racing & Sports Promotions",
          apiKey: "racingPromos",
          active: false,
        },
        {
          key: "topic_6",
          listName: "deposit_promos",
          title: "Exclusive Deposit & Bonus/Free Bet Offers",
          apiKey: "depositPromos",
          active: false,
        },
      ],
      emailMarketing: false,
      smsMarketing: false,
    };
  },
  computed: {
    ...mapState("user/customerIo", ["loading", "saving"]),
    marketingOptIn() {
      return this.emailMarketing || this.smsMarketing;
    },
    userId() {
      return this.$route.params.id;
    },
  },
  methods: {
    ...mapActions("user/customerIo", ["getSubscription", "updateSubscription"]),
    async fetchSubscription() {
      const d = await this.getSubscription(this.userId);
      if (!d) {
        this.$bvModal.hide("marketing-prefs-modal");
        return;
      }
      this.unsubscribed = d.unsubscribed ?? false;
      this.subscriptionTopics.forEach((t) => {
        t.active = d.topics?.[t.apiKey] ?? false;
      });
      this.emailMarketing = d.marketing?.emailMarketingOptIn ?? false;
      this.smsMarketing = d.marketing?.smsMarketingOptIn ?? false;
    },
    async save() {
      const res = await this.updateSubscription({
        userId: this.userId,
        payload: {
          ...Object.fromEntries(
            this.subscriptionTopics.map((t) => [t.apiKey, t.active])
          ),
          emailMarketingOptIn: this.emailMarketing,
          smsMarketingOptIn: this.smsMarketing,
          unsubscribed: this.unsubscribed,
        },
      });
      if (res !== undefined) {
        this.$toast.success("Marketing preferences updated");
        this.$bvModal.hide("marketing-prefs-modal");
      }
    },
    resetState() {
      Object.assign(this.$data, this.$options.data.call(this));
    },
  },
};
</script>

<style lang="scss" scoped>
.mp-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px;
}

.mp-body {
  background: #fff;
  padding: 16px;
}

.mp-section-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #aaa;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.mp-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  margin-bottom: 8px;
}

.mp-card-derived {
  border: 1px dashed #dee2e6;
  align-items: flex-start;
}

.mp-card-unsubscribe {
  background: #f8f9fa;
  border-color: #e9ecef;
  align-items: flex-start;

  &.mp-card-danger {
    background: #fff5f5;
    border-color: #f5c6cb;
  }
}

.mp-title {
  font-weight: 600;
  font-size: 14px;
  color: #1a1f36;
  margin-bottom: 8px;
}

.mp-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.mp-chip {
  background: #e9ecef;
  color: #6c757d;
  font-family: "Courier New", monospace;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
}

.mp-arrow {
  color: #adb5bd;
  font-size: 13px;
}

.mp-synced {
  font-size: 11px;
  color: #28a745;
  font-weight: 500;
}

.mp-derived-badge {
  background: #e8e4ff;
  color: #6f42c1;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.mp-derived-desc {
  font-size: 11px;
  color: #fd7e14;
  margin: 0 0 8px;
}

.mp-derived-value {
  background: #e9ecef;
  color: #1a1f36;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  white-space: nowrap;
  margin-top: 4px;
}
</style>

<style lang="scss">
#marketing-prefs-modal .mp-modal-body-scroll {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
